import React from "react";
import { useSelector } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RootState } from "../store";
import { useGetProjectsQuery } from "../store/api/projects";
import { useGetWorkspaceMembersQuery } from "../store/api/workspaces";
import { Filter, Calendar as CalendarIcon, X } from "lucide-react";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { ja, enUS } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { User, Project } from "../types";

interface FilterPanelProps {
  hideProjectFilter?: boolean;
  projectMembers?: User[];  // Optional: project members for assignee filter (used when provided, e.g., on project details page)
  projects?: Project[];     // Optional: projects list (overrides API fetch)
  users?: User[];           // Optional: workspace users list for assignee filter (overrides API fetch and projectMembers)
}

export default function FilterPanel({
  hideProjectFilter = false,
  projectMembers,
  projects: propsProjects,
  users: propsUsers
}: FilterPanelProps) {
  const { filters, setFilters, currentUser } = useApp();
  const { t, language } = useLanguage();
  const dateLocale = language === "ja" ? ja : enUS;

  // Calendar popover states
  const [startDateOpen, setStartDateOpen] = React.useState(false);
  const [endDateOpen, setEndDateOpen] = React.useState(false);

  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';

  // Fetch projects from API if not provided via props
  const { data: apiProjects = [] } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId || !!propsProjects }
  );

  // Fetch workspace users from API if not provided via props
  const { data: apiUsers = [] } = useGetWorkspaceMembersQuery(
    workspaceId,
    { skip: !workspaceId || !!propsUsers }
  );

  // Use provided props or API data
  const projects = propsProjects || apiProjects.filter(p => !p.isArchived);
  
  // For assignee filter: use projectMembers if provided (for project details page),
  // otherwise use propsUsers, otherwise use API users
  const users = projectMembers || propsUsers || apiUsers;

  // Return early if currentUser is not loaded yet
  if (!currentUser) {
    return null;
  }

  const handleDateRangeChange = (start: Date | undefined, end: Date | undefined, closeStartPopover = false, closeEndPopover = false) => {
    if (start || end) {
      setFilters({
        ...filters,
        dateRange: {
          type: "custom",
          customStart: start,
          customEnd: end,
        },
      });
    } else {
      setFilters({
        ...filters,
        dateRange: {
          type: "all",
        },
      });
    }

    // Close popovers after selection
    if (closeStartPopover) {
      setStartDateOpen(false);
    }
    if (closeEndPopover) {
      setEndDateOpen(false);
    }
  };

  const clearDateRange = () => {
    setFilters({
      ...filters,
      dateRange: {
        type: "all",
      },
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="mb-4 flex items-center gap-2">
        <Filter className="w-4 h-4 text-gray-600" />
        <h2 className="text-gray-900">{t('common.filter')}</h2>
      </div>

      <div className="space-y-6">
        {/* プロジェクトフィルター */}
        {!hideProjectFilter && (
          <div>
            <Label className="mb-3 block">{t('tasks.project')}</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all-projects"
                  checked={filters.projectIds.length === 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFilters({ ...filters, projectIds: [] });
                    }
                  }}
                />
                <label
                  htmlFor="all-projects"
                  className="text-gray-700 cursor-pointer"
                >
                  {t('projects.allProjects')}
                </label>
              </div>
              {projects.map((project) => (
                <div key={project.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`project-${project.id}`}
                    checked={filters.projectIds.includes(project.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFilters({
                          ...filters,
                          projectIds: [...filters.projectIds, project.id],
                        });
                      } else {
                        setFilters({
                          ...filters,
                          projectIds: filters.projectIds.filter(
                            (id) => id !== project.id
                          ),
                        });
                      }
                    }}
                  />
                  <label
                    htmlFor={`project-${project.id}`}
                    className="text-gray-700 cursor-pointer"
                  >
                    {project.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 期限フィルター */}
        <div>
          <Label className="mb-3 block">{t('tasks.dateFilter')}</Label>
          <div className="space-y-2">
            {/* 開始日 */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">{t('tasks.startDate')}</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange.customStart ? (
                      format(filters.dateRange.customStart, "PPP", { locale: dateLocale })
                    ) : (
                      <span className="text-gray-500">{t('common.select')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange.customStart}
                    onSelect={(date) =>
                      handleDateRangeChange(date, filters.dateRange.customEnd, true)
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* 終了日 */}
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">{t('tasks.endDate')}</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.dateRange.customEnd ? (
                      format(filters.dateRange.customEnd, "PPP", { locale: dateLocale })
                    ) : (
                      <span className="text-gray-500">{t('common.select')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.dateRange.customEnd}
                    onSelect={(date) =>
                      handleDateRangeChange(filters.dateRange.customStart, date, false, true)
                    }
                    disabled={(date) =>
                      filters.dateRange.customStart
                        ? date < filters.dateRange.customStart
                        : false
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* クリアボタン */}
            {(filters.dateRange.customStart || filters.dateRange.customEnd) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={clearDateRange}
              >
                <X className="mr-2 h-4 w-4" />
                {t('tasks.clearDateFilter')}
              </Button>
            )}
          </div>
        </div>

        {/* 担当者フィルター */}
        <div>
          <Label className="mb-3 block">{t('tasks.assigneeFilter')}</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="all-assignees"
                checked={filters.assigneeIds.length === 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setFilters({ ...filters, assigneeIds: [] });
                  }
                }}
              />
              <label
                htmlFor="all-assignees"
                className="text-gray-700 cursor-pointer"
              >
                {t('tasks.allAssignees')}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="my-tasks"
                checked={
                  filters.assigneeIds.length === 1 &&
                  filters.assigneeIds[0] === currentUser.id
                }
                onCheckedChange={(checked) => {
                  if (checked) {
                    setFilters({ ...filters, assigneeIds: [currentUser.id] });
                  } else {
                    setFilters({ ...filters, assigneeIds: [] });
                  }
                }}
              />
              <label
                htmlFor="my-tasks"
                className="text-gray-700 cursor-pointer"
              >
                {t('tasks.myTasks')}
              </label>
            </div>
            {users
              .filter((user) => user.id !== currentUser.id)
              .map((user) => (
                <div key={user.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`user-${user.id}`}
                    checked={filters.assigneeIds.includes(user.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFilters({
                          ...filters,
                          assigneeIds: [...filters.assigneeIds, user.id],
                        });
                      } else {
                        setFilters({
                          ...filters,
                          assigneeIds: filters.assigneeIds.filter(
                            (id) => id !== user.id
                          ),
                        });
                      }
                    }}
                  />
                  <label
                    htmlFor={`user-${user.id}`}
                    className="text-gray-700 cursor-pointer"
                  >
                    {user.name}
                  </label>
                </div>
              ))}
          </div>
        </div>

        {/* ステータスフィルター */}
        <div>
          <Label className="mb-3 block">{t('tasks.statusFilter')}</Label>
          <Select
            value={filters.status}
            onValueChange={(value) => {
              setFilters({
                ...filters,
                status: value as any,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">{t('tasks.pending')}</SelectItem>
              <SelectItem value="completed">{t('tasks.completed')}</SelectItem>
              <SelectItem value="all">{t('common.all')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* タスクタイプフィルター */}
        <div>
          <Label className="mb-3 block">{t('tasks.taskTypeFilter')}</Label>
          <Select
            value={filters.taskType || 'all'}
            onValueChange={(value) => {
              setFilters({
                ...filters,
                taskType: value as any,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="recurring">{t('tasks.recurringTask')}</SelectItem>
              <SelectItem value="oneTime">{t('tasks.oneTimeTask')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
